import { useQuery } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import { ArrowRight, HeartHandshake, MapPin } from 'lucide-react'
import { Link } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { CACHE_TIMES } from '@/lib/queryClient'
import SmartImage from '@/components/SmartImage'

type Partner = {
  id: string
  slug: string
  name: string
  preview_image_url: string | null
  image_settings: { desktop: any; mobile: any } | null
}

export default function HomePartnersCard() {
  const { data: partners } = useQuery({
    queryKey: ['partners', 'home-card'],
    queryFn: async (): Promise<Partner[]> => {
      const { data, error } = await supabase
        .from('partners')
        .select(
          'id, slug, name, preview_image_url, image_settings, display_order'
        )
        .eq('is_active', true)
        .order('display_order', { ascending: true })
      if (error) return []
      return (data as any) || []
    },
    ...CACHE_TIMES.STATIC,
  })

  if (!partners || partners.length === 0) return null

  const withImage = partners.filter((p) => p.preview_image_url)
  const [hero, ...rest] = withImage

  return (
    <section className="relative px-4 py-16 sm:px-6 sm:py-20 lg:px-8 lg:py-24">
      <div className="mx-auto max-w-6xl">
        <motion.article
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.55, ease: 'easeOut' }}
          className="relative overflow-hidden rounded-3xl border border-garage-accent/30 bg-gradient-to-br from-garage-dark via-garage-metal to-garage-mud shadow-2xl"
        >
          {/* Workshop texture — matches the homepage's other dark sections */}
          <div className="pointer-events-none absolute inset-0 bg-garage-texture opacity-10" />
          <div className="relative grid grid-cols-1 gap-0 lg:grid-cols-[1.45fr_1fr]">
            {/* LEFT — bento mosaic of partner previews (fills the old dark void) */}
            <div className="order-2 grid grid-cols-2 gap-3 p-4 sm:gap-4 sm:p-6 lg:order-1">
              {hero && (
                <PartnerTile
                  partner={hero}
                  className="col-span-2 h-44 sm:h-52"
                  priority
                />
              )}
              {rest.map((p, i) => (
                <PartnerTile
                  key={p.id}
                  partner={p}
                  className="h-28 sm:h-36"
                  delay={0.1 + i * 0.06}
                />
              ))}
            </div>

            {/* RIGHT — text + CTA panel */}
            <div className="order-1 flex flex-col justify-center border-b border-garage-accent/20 p-6 sm:p-8 lg:order-2 lg:border-b-0 lg:border-l lg:p-10">
              <div className="mb-5 inline-flex w-fit items-center gap-2 rounded-full border border-garage-accent/40 bg-garage-cream/5 px-3.5 py-1.5">
                <HeartHandshake className="h-3.5 w-3.5 text-garage-accent" />
                <span className="text-[11px] font-bold uppercase tracking-widest text-garage-accent">
                  Our Local Network
                </span>
              </div>

              <h2 className="font-garage-title text-3xl font-black leading-tight text-garage-cream sm:text-4xl">
                Trusted partners in{' '}
                <span className="text-garage-accent">Vang Vieng</span>
              </h2>

              <p className="mt-3 text-sm text-garage-cream/80 sm:text-base">
                Hand-picked local operators we ride, eat and fly with — the
                people who make a Terra Lao trip run smoothly.
              </p>

              <div className="mt-7 flex flex-wrap items-center gap-3">
                <Link
                  to="/partners"
                  className="group/cta inline-flex items-center gap-2 rounded-full bg-garage-accent px-5 py-2.5 text-sm font-bold text-garage-cream shadow-lg shadow-garage-accent/30 transition-all duration-300 hover:-translate-y-0.5 hover:bg-garage-primary hover:shadow-xl hover:shadow-garage-accent/50 motion-reduce:transition-none motion-reduce:hover:translate-y-0"
                >
                  Meet our {withImage.length} partners
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover/cta:translate-x-0.5 motion-reduce:transition-none motion-reduce:group-hover/cta:translate-x-0" />
                </Link>
                <span className="inline-flex items-center gap-1.5 text-xs text-garage-cream/60">
                  <MapPin className="h-3.5 w-3.5 text-garage-accent" />
                  Vang Vieng · Laos
                </span>
              </div>
            </div>
          </div>
        </motion.article>
      </div>
    </section>
  )
}

function PartnerTile({
  partner,
  className = '',
  priority = false,
  delay = 0,
}: {
  partner: Partner
  className?: string
  priority?: boolean
  delay?: number
}) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.97 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      transition={{ delay, duration: 0.4, ease: 'easeOut' }}
      className={`group/tile relative overflow-hidden rounded-xl border border-garage-accent/20 ${className}`}
    >
      {partner.preview_image_url && (
        <SmartImage
          src={partner.preview_image_url}
          alt={`${partner.name} — Vang Vieng partner`}
          settings={partner.image_settings ?? undefined}
          className="absolute inset-0 h-full w-full transition-transform duration-700 group-hover/tile:scale-105 motion-reduce:transition-none motion-reduce:group-hover/tile:scale-100"
          loading="lazy"
          fetchPriority={priority ? 'high' : 'auto'}
        />
      )}
      {/* Bottom gradient so the name stays legible over any photo */}
      <div className="absolute inset-0 bg-gradient-to-t from-garage-dark/90 via-garage-dark/20 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 flex items-center gap-2 p-3">
        <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-garage-accent shadow-[0_0_10px_rgba(196,90,26,0.6)]" />
        <span
          className={`font-bold text-garage-cream drop-shadow-garage ${
            priority ? 'text-base sm:text-lg' : 'text-sm'
          }`}
        >
          {partner.name}
        </span>
      </div>
    </motion.div>
  )
}
